import { AxiosResponse } from 'axios'
import { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh'
import * as z from 'zod'
import { UserOptionsMe } from '~/shared/api/core/store'
import {
  dateSchema,
  phoneScheme,
  uuidOptionsSchema,
} from '~/shared/lib/schemas'
import { buildI18nMessage } from '../lib/i18n'
import { ApiModel } from './core'

const attributeSchema = z.object({
  email: z.string().email(buildI18nMessage('validate.invalid email address')),
  password: z.string().min(5, {
    message: buildI18nMessage(
      'validate.password must be at least 5 characters long',
    ),
  }),
  name: z.string(),
  phone: phoneScheme,
  telegramUserId: z.number().nullable(),
})

const relationsSchema = z.object({
  regionsIds: uuidOptionsSchema,
  rolesIds: uuidOptionsSchema,
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)
  .merge(relationsSchema)

export type UserAttributes = z.infer<typeof schema>

export class User extends ApiModel<typeof schema> {
  static jsonApiType = 'users'

  static schema = schema

  static async login(credentials: { email: Email; password: string }) {
    const url = `${this.getJsonApiUrl()}/login`
    const client = this.httpClient.getImplementingClient()
    return client
      .post<
        typeof credentials,
        AxiosResponse<{ token: string; id: UniqueId; email: string }>
      >(url, credentials, {
        skipAuthRefresh: true,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json',
        },
      } as AxiosAuthRefreshRequestConfig)
      .then(({ data }) => data)
  }

  static async me() {
    const url = `${this.getJsonApiUrl()}/me`
    const client = User.httpClient.getImplementingClient()
    return client.get<UserOptionsMe>(url).then(({ data }) => data)
  }

  static async logout() {
    const url = `${this.getJsonApiUrl()}/logout`
    return User.effectiveHttpClient.post(url)
  }

  getEmail(): string {
    return this.getAttribute('email')
  }

  getName(): string {
    return this.getAttribute('name')
  }

  getPhone(): string | undefined {
    return this.getAttribute('phone')
  }

  getOption(): { id: UniqueId; label: string } {
    return {
      id: this.getApiId() as UniqueId,
      label: this.getName(),
    }
  }

  static async fetchOptions() {
    const response = await User.get(1)
    return response.getData().map((o) => o.getOption())
  }
}
